export interface EmailAccount {
  id: string;
  email: string;
  accountName: string;
  displayName: string | null;
  provider: string;
  color: string;
  sortOrder: number;
  imapHost: string;
  imapPort: number;
  imapSecure: boolean;
  smtpHost: string;
  smtpPort: number;
  smtpSecure: boolean;
  username: string;
  isActive: boolean;
  connectionStatus: 'pending' | 'connected' | 'error';
  lastConnectedAt: string | null;
  lastSyncAt: string | null;
  lastError: string | null;
  configuredBy: string | null;
  createdAt: string;
}

export interface PhoneNumber {
  id: string;
  label: string;
  phone_number: string;
  type: 'business' | 'support' | 'sales' | 'fax' | 'google_voice';
  is_active: boolean;
  sort_order: number;
  notes: string | null;
  configured_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface SupportInteraction {
  id: string;
  external_id: string;
  type: 'voicemail' | 'email';
  direction: 'inbound' | 'outbound';
  from_address: string;
  to_address: string;
  subject: string;
  body_text: string;
  body_html: string;
  voicemail_transcript: string | null;
  voicemail_audio_url: string | null;
  caller_phone: string | null;
  member_user_id: string | null;
  member_email: string | null;
  member_name: string | null;
  gmail_thread_id: string | null;
  is_read: boolean;
  is_resolved: boolean;
  resolved_by: string | null;
  resolved_at: string | null;
  resolution_notes: string | null;
  tags: string[];
  callback_initiated: boolean;
  callback_at: string | null;
  callback_status: string | null;
  received_at: string;
  created_at: string;
  updated_at: string;
  email_account_id: string | null;
}

export interface InboxData {
  voicemails: SupportInteraction[];
  emails: SupportInteraction[];
  total: number;
  gmailSynced: number;
  fetchSource?: string;
}

export interface EmailConnectionStatus {
  configured: boolean;
  email: string | null;
  status: string | null;
  lastError: string | null;
}

export interface EmailAccountStatus {
  id: string;
  email: string;
  accountName: string;
  displayName: string | null;
  provider: string;
  color: string;
  sortOrder: number;
  status: string;
  lastConnectedAt: string | null;
  lastSyncAt: string | null;
  lastError: string | null;
}

export interface Stats {
  unreadCount: number;
  openCount: number;
  unreadVoicemails: number;
  unreadEmails: number;
  total: number;
  googleVoiceConfigured: boolean;
  emailConnection?: EmailConnectionStatus;
  emailAccounts?: EmailAccountStatus[];
}

export interface ProviderInfo {
  provider: string;
  name: string;
  imap: { host: string; port: number; secure: boolean };
  smtp: { host: string; port: number; secure: boolean };
  requiresAppPassword: boolean;
  appPasswordUrl?: string;
  securityUrl?: string;
  notes: string;
}

export type FilterType = 'all' | 'unread' | 'unresolved' | 'voicemail' | 'email';
export type FolderType = 'inbox' | 'sent' | 'drafts' | 'all';
export type TabType = 'emails' | 'funnels';

export interface SupportTag {
  id: string;
  name: string;
  color: string;
  created_at: string;
}

export interface SupportDraft {
  id: string;
  email_account_id: string | null;
  account_email: string | null;
  to_address: string;
  cc: string | null;
  subject: string | null;
  body_html: string | null;
  body_text: string | null;
  in_reply_to_external_id: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface SupportFunnelStage {
  id: string;
  funnel_id: string;
  name: string;
  stage_order: number;
  color: string | null;
  created_at: string;
}

export interface SupportFunnel {
  id: string;
  name: string;
  description: string | null;
  color: string;
  is_active: boolean;
  sort_order: number;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  stages: SupportFunnelStage[];
}

export interface FunnelBoardCard {
  assignment_id: string;
  stage_id: string;
  interaction_id: string;
  external_id: string;
  type: string;
  direction: string;
  from_address: string;
  to_address: string;
  subject: string | null;
  tags: string[];
  is_read: boolean;
  is_resolved: boolean;
  member_name: string | null;
  member_email: string | null;
  received_at: string;
  assigned_at: string;
  moved_at: string;
}

export interface FunnelBoardColumn extends SupportFunnelStage {
  emails: FunnelBoardCard[];
}

export const ACCOUNT_COLORS = [
  '#6366F1', // Indigo
  '#10B981', // Emerald
  '#F59E0B', // Amber
  '#EF4444', // Red
  '#EC4899', // Pink
  '#8B5CF6', // Violet
  '#06B6D4', // Cyan
  '#F97316', // Orange
];
